import React, { useEffect, useRef, useState } from "react";
import {
  Col,
  Row,
  Form,
  Select,
  Button,
  Spin,
  Checkbox,
  message as AntdAlert,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import StudentSheet from "./StudentSheet";
import StudentCurriculums from "./StudentCurriculums";
import SemesterSection from "./SemesterSection";
import OverallSummary from "./OverallSummary";

import {
  fetchClassesStudents,
  reviewReport,
  generateReport,
} from "./redux/actions";
import { setSelectedClass, setSelectedStudent } from "./redux/actions";
import { fetchReportDetail } from "../ReportDetail/redux/actions";
import TermReportTable from "../TermReport/table";

const SemesterReportTable = () => {
  const [form] = Form.useForm();
  const reportRef = useRef();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(true);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [isReviewReport, setIsReviewReport] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const {
    classes = [],
    filteredStudents = [],
    loading,
  } = useSelector((state) => state.semesterReportReducer);

  const reportData = useSelector((state) => state.semesterReportReducer);

  const { allClasses } = useSelector((state) => state.classesReducer);

  const { allReportDetail } = useSelector((state) => state.reportDetailReducer);

  const filteredReportDetail = selectedReportType
    ? allReportDetail.filter(
      (report) => report.report_type === selectedReportType
    )
    : [];

  const reportType = [
    { label: "TERM", value: "term" },
    { label: "SEMESTER", value: "semester" },
  ];



  //zee fixes
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    setLoadingPDF(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const pages = reportRef.current.querySelectorAll(".pdf-page");

      await document.fonts.ready;
      await new Promise((r) => setTimeout(r, 300));

      let firstPage = true;

      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: "#fff",
          scrollY: -window.scrollY,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        if (!firstPage) pdf.addPage();
        firstPage = false;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 1) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }

      const pageCount = pdf.internal.getNumberOfPages();
      const lastPage = pdf.internal.pages[pageCount];
      if (lastPage && lastPage.length <= 2) {
        pdf.deletePage(pageCount);
      }

      pdf.save("student-report.pdf");
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setLoadingPDF(false);
    }
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    dispatch(fetchClassesStudents());
    dispatch(fetchReportDetail());
  }, [dispatch]);

  const { Option } = Select;

  const normalizeClassValue = (classValue) => {
    if (!classValue) return undefined;
    return classValue.length === 1 ? classValue[0] : classValue;
  };

  const handleReviewReport = () => {
    console.log("Review Report clicked ");
    setIsReviewReport(true);
    const values = form.getFieldsValue();

    const classValue = normalizeClassValue(values.class);

    if (!classValue && !values.student) {
      const payload = {
        report_type: values.report_type,
        report_type_id: values.report,
        send_email: "true",
      };
      dispatch(reviewReport(payload));
      setCollapsed(false);
      return;
    }

    const payload = {
      class_id: classValue,
      student_id: values.student,
      report_type: values.report_type,
      report_type_id: values.report,
      send_email: "true",
    };

    dispatch(reviewReport(payload));
    setCollapsed(false);
  };

  const handleGenerateReport = () => {
    const values = form.getFieldsValue();

    if (!values.class && !values.student) {
      const payload = {
        report_type: values.report_type,
        report_type_id: values.report,
      };
      dispatch(generateReport(payload));
      return;
    }

    const payload = {
      class_ids: values.class
        ? Array.isArray(values.class)
          ? values.class
          : [values.class]
        : [],
      report_type: values.report_type,
      report_type_id: values.report_type_id || values.report,
    };

    dispatch(generateReport(payload));
  };

  const handleSearch = (value) => {
    const searchText = value.trim().toLowerCase();
    setSearchActive(!!searchText);

    if (searchText) {
      const filtered = classes.filter(({ name, id }) =>
        `${name} - ${id}`.toLowerCase().includes(searchText)
      );
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses([]);
    }
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    const selected = form.getFieldValue("class") || [];

    if (filteredClasses.length === 0) return;

    const filteredIds = filteredClasses.map((cls) => cls._id);

    let updatedSelection = [];

    if (isChecked) {
      updatedSelection = Array.from(new Set([...selected, ...filteredIds]));
    } else {
      updatedSelection = selected.filter((id) => !filteredIds.includes(id));
    }

    form.setFieldsValue({ class: updatedSelection });
    dispatch(setSelectedClass(updatedSelection));
    setSelectAllChecked(isChecked);
  };

  const handleClassChange = (selected) => {
    form.setFieldsValue({ class: selected });
    dispatch(setSelectedClass(selected));

    if (
      filteredClasses.length > 0 &&
      filteredClasses.every((cls) => selected.includes(cls._id))
    ) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  };

  const handleDropdownVisibleChange = (open) => {
    if (!open) {
      setSearchActive(false);
      setFilteredClasses([]);
      setSelectAllChecked(false);
    } else {
      const selected = form.getFieldValue("class") || [];
      if (
        filteredClasses.length > 0 &&
        filteredClasses.every((cls) => selected.includes(cls._id))
      ) {
        setSelectAllChecked(true);
      } else {
        setSelectAllChecked(false);
      }
    }
  };

  const handleClearAll = () => {
    form.setFieldsValue({ class: [] });
    dispatch(setSelectedClass([]));
    setSelectAllChecked(false);
  };

  return (
    <div className="page-top-div">
      <span className="text-span">Student Report</span>
      <Spin spinning={loading || loadingPDF} size="medium" fullscreen>
        <Row justify="center" className="production-manager-parent-row">
          <Col span={24} className="heading-col">
            <Row align="middle" justify="space-between">
              <Col>
                <span className="heading-span">Student Report Manager</span>
              </Col>
            </Row>
          </Col>

          <Col
            md={24}
            lg={23}
            xl={20}
            xxl={20}
            style={{ minHeight: "10vh", marginTop: "10px" }}
          >
            <Form
              form={form}
              layout="vertical"
              onValuesChange={(changed, allValues) => {
                if (
                  (!allValues.class || allValues.class.length === 0) &&
                  !allValues.student
                ) {
                  setIsReviewReport(false);
                }
              }}
            >
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <div className="special-table-header-text">
                    Select Report Type
                  </div>
                  <Form.Item
                    name="report_type"
                    rules={[
                      {
                        required: true,
                        message: "Please select a report type",
                      },
                    ]}
                  >
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      placeholder="Select Report Types"
                      optionLabelProp="label"
                      onChange={(value) => setSelectedReportType(value)}
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownClassName="select-dropdown-custom"
                    >
                      {reportType.map(({ label, value }) => (
                        <Option key={value} value={value} label={label}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <div className="special-table-header-text">
                    Select Report Period
                  </div>
                  <Form.Item
                    name="report"
                    rules={[
                      {
                        required: true,
                        message: "Please select a report period",
                      },
                    ]}
                  >
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      placeholder="Select Report Period"
                      optionLabelProp="label"
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownClassName="select-dropdown-custom"
                    >
                      {filteredReportDetail.map(({ _id, title }) => (
                        <Option key={_id} value={_id} label={title}>
                          {title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <div className="special-table-header-text">Select Class</div>
                    </Col>

                    <Col>
                      {form.getFieldValue("class")?.length > 0 && (
                        <span className="special-table-header-text" onClick={handleClearAll} style={{ fontWeight: "bold", cursor: "pointer" }}>Clear All</span>
                      )}
                    </Col>
                  </Row>

                  <Form.Item name="class">
                    <Select
                      size="small"
                      mode="multiple"
                      allowClear
                      showSearch
                      placeholder="Search or select classes"
                      optionLabelProp="label"
                      onSearch={handleSearch}
                      onDropdownVisibleChange={handleDropdownVisibleChange}
                      onChange={handleClassChange}
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownRender={(menu) => (
                        <>
                          {searchActive && (
                            <div
                              style={{
                                padding: "6px 12px",
                                borderBottom: "1px solid #f0f0f0",
                                background: "#fafafa",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                checked={selectAllChecked}
                                onChange={handleSelectAllChange}
                              >
                                <b>Select All</b>
                              </Checkbox>
                            </div>
                          )}
                          {menu}
                        </>
                      )}
                    >
                      {classes.map(({ _id, id, name }) => (
                        <Option key={_id} value={_id} label={`${name} - ${id}`}>
                          {`${name} - ${id}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item name="classSearch" hidden>
                    <input type="hidden" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <div className="special-table-header-text">
                    Select Student
                  </div>
                  <Form.Item name="student">
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      placeholder="Select Student"
                      optionLabelProp="label"
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownClassName="select-dropdown-custom"
                    >
                      {form.getFieldValue("class")?.length === 1
                        ? filteredStudents.map(({ id, name, login_id }) => (
                          <Option
                            key={id}
                            value={id}
                            label={`${name} - ${login_id}`}
                          >
                            {`${name} - ${login_id}`}
                          </Option>
                        ))
                        : []}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]} align="middle" justify="space-between">
                <Col>
                  {(form.getFieldValue("class")?.length === 1 ||
                    form.getFieldValue("student")) && (
                      <Button
                        onClick={() => {
                          toggleCollapse();
                          handleReviewReport();
                        }}
                        size="medium"
                      >
                        Review Report
                      </Button>
                    )}

                  <Button
                    onClick={handleGenerateReport}
                    size="medium"
                    style={{
                      marginLeft: 10,
                      padding: "0 20px",
                      height: "32px",
                    }}
                  >
                    Generate Report
                  </Button>
                </Col>

                {isReviewReport && (
                  <Col>
                    <Button
                      size="medium"
                      onClick={handleDownloadPDF}
                      style={{ padding: "0 20px", height: "32px" }}
                    >
                      Download PDF
                    </Button>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>

          {!collapsed && (
            <div ref={reportRef} style={{ overflowX: "auto" }}>
              {selectedReportType === "semester" && (
                <>
                  <SemesterSection
                    data={reportData?.classEnrolment}
                    report_type={reportData?.classEnrolment?.report_log?.report_type || form.getFieldValue("report_type")}
                    report_period={filteredReportDetail.find(
                      (r) =>
                        r._id === (reportData?.classEnrolment?.report_log?.report_period || form.getFieldValue("report"))
                    )?.title || ""}
                  />

                  <StudentSheet />

                  <OverallSummary
                    summaryData={reportData?.classEnrolment?.streams?.flatMap(
                      (stream) => stream?.learning_activities || []
                    ) || []}
                    cumulative_grade={reportData?.classEnrolment?.runtime_cumulative_grade}
                    cumulative_grade_distribution={reportData?.classEnrolment?.cumulative_grade_distribution}
                    student={reportData?.classEnrolment?.user}
                    attendance={{
                      attendance_scale: reportData?.classEnrolment?.attendance_scale ?? "-",
                      present_count: reportData?.classEnrolment?.present_count ?? 0,
                      absent_count: reportData?.classEnrolment?.absent_count ?? 0,
                      late_count: reportData?.classEnrolment?.late_count ?? 0,
                    }}
                    report_type={form.getFieldValue("report_type")}
                    report_period={filteredReportDetail.find(
                      (r) => r._id === form.getFieldValue("report")
                    )?.title}
                  />

                  <StudentCurriculums
                    curriculums={reportData.classEnrolment?.curriculums || []}
                    streams={reportData.classEnrolment?.streams || []}
                    report_type={form.getFieldValue("report_type")}
                    report_period={filteredReportDetail.find(
                      (r) => r._id === form.getFieldValue("report")
                    )?.title}
                  />
                </>
              )}

              {selectedReportType === "term" && (
                <TermReportTable
                  data={reportData?.classEnrolment}
                  curriculums={reportData.classEnrolment?.curriculums || []}
                  streams={reportData.classEnrolment?.streams || []}
                  report_type={reportData?.classEnrolment?.report_log?.report_type || form.getFieldValue("report_type")}
                  report_period={filteredReportDetail.find(
                    (r) => r._id === form.getFieldValue("report")
                  )?.title}
                />
              )}
            </div>
          )}

        </Row>
      </Spin>
    </div>
  );
};

export default SemesterReportTable;
